import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import isEmpty from 'lodash/fp/isEmpty';
import isNil from 'lodash/fp/isNil';
import noop from 'lodash/fp/noop';
import ClearableInput from '../clearableInput/ClearableInput';
import { scrollItemIntoView, UP, DOWN } from '../../utils/scrollItemIntoView';
import { useDropDirection } from '../../utils/useDropDirection';
import DropdownHeader from '../selects/DropdownHeader';
import MenuItem from '../menuItems/MenuItem';
import useClickOutside from '../../hooks/useClickOutside';
import AutoSuggestAddons from './AutoSuggestAddons';
import DropdownSpinner from './DropdownSpinner';
import NoItemMessage from './NoItemMessage';
export const AutoSuggest = (props) => {
    const { id, onSuggestionSelected = noop, onSuggestionsFetchRequested = noop, onSuggestionHighlighted = noop, renderSuggestion, customSuggestionKey, getSuggestionValue, suggestions = [], autoDropDirection = true, pullRight = false, dropup = false, isLoading = false, openOnFocus = false, closeOnSelect = true, showSelectedItemsInInput = true, autoComplete = 'off', inputProps = {
        id,
        disabled: false,
        onChange: noop,
        onClear: noop,
        onBlur: noop,
        onFocus: noop,
        placeholder: 'Start typing ...',
        hasError: false,
        tabIndex: 0,
        value: undefined,
        icon: undefined,
    }, leadingInputAddons, trailingInputAddons, noItemMessage, dropdownClassName = '', className = '' } = props, remainingProps = __rest(props, ["id", "onSuggestionSelected", "onSuggestionsFetchRequested", "onSuggestionHighlighted", "renderSuggestion", "customSuggestionKey", "getSuggestionValue", "suggestions", "autoDropDirection", "pullRight", "dropup", "isLoading", "openOnFocus", "closeOnSelect", "showSelectedItemsInInput", "autoComplete", "inputProps", "leadingInputAddons", "trailingInputAddons", "noItemMessage", "dropdownClassName", "className"]);
    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState(inputProps.value || '');
    const [highlightedItemIndex, setHighlightedItemIndex] = useState(-1);
    const [direction, setDirection] = useState(DOWN);
    const componentRef = useClickOutside(() => closeMenu());
    const dropdownMenuRef = useRef(null);
    const inputRef = useRef(null);
    const optionNodesRef = useRef([]);
    // Update internal value state from outside when external input value is given.
    // This is important for when this component is used as a controlled component
    const externalValue = inputProps.value;
    if (!isNil(externalValue) && value !== externalValue) {
        setValue(externalValue !== null && externalValue !== void 0 ? externalValue : '');
    }
    // Fetch suggestions on mounting the component by using the external
    // fetch callback
    useEffect(() => onSuggestionsFetchRequested({ value }), []);
    // When a new suggestion is highlighted it may be not in view as the dropdown menu
    // has an overflow and can scroll. In this case, we need to scroll to the highlighted suggestion
    // so the user can see it
    useEffect(() => {
        const focusedItem = getFocusedOptionNode();
        scrollItemIntoView(direction, dropdownMenuRef.current, focusedItem);
    }, [highlightedItemIndex, direction]);
    // Used to store the list item reference to allow highlighting a single node.
    // Update that list of nodes again when the suggestions change from the outside.
    useEffect(() => {
        var _a;
        const optionNodes = Array.from(((_a = dropdownMenuRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByTagName('li')) || []);
        optionNodesRef.current = optionNodes;
    }, [suggestions]);
    // Overwrite position of dropdown menu in case auto drop is enabled
    const dropDirection = useDropDirection({ pullRight, dropup, autoDropDirection, dropdownMenuRef }, [suggestions]);
    const openMenu = () => {
        if (isOpen) {
            return;
        }
        setIsOpen(true);
    };
    const closeMenu = () => {
        setIsOpen(false);
    };
    const handleFocus = (event) => {
        if (openOnFocus) {
            openMenu();
        }
        if (inputProps.onFocus) {
            inputProps.onFocus(event);
        }
    };
    const handleKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown': {
                // select below on arrow down key
                const newHighlightedIndex = highlightedItemIndex + 1;
                if (isOpen && suggestions.length > newHighlightedIndex) {
                    setHighlightedItemIndex(newHighlightedIndex);
                    handleHighlightedSuggestionChanged(Number(newHighlightedIndex));
                    setDirection(DOWN);
                }
                return;
            }
            case 'ArrowUp':
                // select above on arrow up key
                if (isOpen && highlightedItemIndex > 0) {
                    const newHighlightedIndex = highlightedItemIndex - 1;
                    setHighlightedItemIndex(newHighlightedIndex);
                    handleHighlightedSuggestionChanged(Number(newHighlightedIndex));
                    setDirection(UP);
                }
                return;
            case 'Enter':
                // select on enter
                if (isOpen && suggestions[highlightedItemIndex]) {
                    onSuggestionClicked(event, suggestions[highlightedItemIndex]);
                }
                else {
                    openMenu();
                }
                break;
            case 'Escape':
            case 'Tab':
                // close on esc key or on tab
                closeMenu();
                break;
            default:
                break;
        }
    };
    const getFocusedOptionNode = () => {
        return [...optionNodesRef.current].find((item) => item.className.includes('active'));
    };
    const handleInputChange = (newValue, event) => {
        if (value !== newValue) {
            updateInputValue(newValue, event);
        }
    };
    const updateInputValue = (newValue, event, shouldCloseMenu = false) => {
        const closeMenuEventually = shouldCloseMenu ? false : isOpen;
        setIsOpen(!isOpen ? true : closeMenuEventually);
        setValue(newValue);
        setHighlightedItemIndex(-1);
        // Fire callback so the parent component can filter the suggestions accordingly
        onSuggestionsFetchRequested({ value: newValue });
        handleHighlightedSuggestionChanged(-1);
        // Trigger onChange callback to notify usage component only when value has actually changed
        const { value: newExternalValue, onChange = () => { } } = inputProps;
        if (newValue !== newExternalValue) {
            onChange(event, { newValue });
        }
    };
    const handleHighlightedSuggestionChanged = (currentHighlightedItemIndex) => {
        const currentHighlightedSuggestion = currentHighlightedItemIndex === -1 ? null : suggestions[currentHighlightedItemIndex];
        onSuggestionHighlighted(currentHighlightedSuggestion);
    };
    const clearInputValue = (event) => {
        setValue('');
        setHighlightedItemIndex(-1);
        // Focus the input field after clearing it's value in order to allow
        // entering new values right away
        if (inputRef.current) {
            inputRef.current.focus();
        }
        // Reset filter value so all suggestions are shown again
        onSuggestionsFetchRequested({ value: '' });
        handleHighlightedSuggestionChanged(-1);
        // Additionally, fire the onClear callback as the service may react on it
        if (inputProps.onClear) {
            inputProps.onClear(event);
        }
    };
    const getDefaultSuggestionValue = (suggestion) => {
        var _a, _b;
        return (_b = (_a = suggestion.label) !== null && _a !== void 0 ? _a : suggestion[customSuggestionKey !== null && customSuggestionKey !== void 0 ? customSuggestionKey : '']) !== null && _b !== void 0 ? _b : '';
    };
    const onSuggestionClicked = (event, suggestion) => {
        const getSuggestionValueFunction = getSuggestionValue ? getSuggestionValue : getDefaultSuggestionValue;
        const suggestionValue = getSuggestionValueFunction(suggestion);
        updateInputValue(suggestionValue, event, closeOnSelect);
        // fire callback with the selected item
        onSuggestionSelected(event, {
            suggestion,
            suggestionValue,
        });
    };
    const renderInput = () => {
        const { id: inputPropId, icon, value: ignoredExternalValue } = inputProps, remainingInputProps = __rest(inputProps, ["id", "icon", "value"]);
        const clearableProps = Object.assign({ id: id !== null && id !== void 0 ? id : inputPropId }, remainingInputProps);
        const input = (_jsx(ClearableInput, Object.assign({}, clearableProps, { ref: inputRef, autoComplete: autoComplete, onChange: handleInputChange, onClear: clearInputValue, onFocus: handleFocus, onClick: openMenu, value: showSelectedItemsInInput ? value : '' })));
        if (icon || leadingInputAddons || trailingInputAddons) {
            return (_jsx(AutoSuggestAddons, Object.assign({ icon: icon, leadingInputAddons: leadingInputAddons, trailingInputAddons: trailingInputAddons }, { children: input })));
        }
        return input;
    };
    const renderDefaultSuggestion = (suggestion) => suggestion.label;
    const renderDropdownMenu = () => {
        const dropdownMenuClasses = classNames('dropdown-menu', dropDirection.pullRight && 'pull-right', dropdownClassName);
        if (isLoading) {
            return _jsx(DropdownSpinner, { className: dropdownMenuClasses });
        }
        const hasNoSuggestions = isEmpty(suggestions);
        if (hasNoSuggestions && !noItemMessage) {
            return null;
        }
        return (_jsxs("ul", Object.assign({ role: 'menu', className: dropdownMenuClasses, ref: dropdownMenuRef }, { children: [hasNoSuggestions && noItemMessage && _jsx(NoItemMessage, { message: noItemMessage }, 'NoItemMessage'), suggestions.map((suggestion, index) => {
                    var _a;
                    // In case a custom renderer is used but the customSuggestionKey is not set, throw an error
                    if (renderSuggestion && !suggestion.label && !customSuggestionKey) {
                        throw new Error('The "customSuggestionKey" need to be set when using a custom renderer ' +
                            'and not using the "label" prop for suggestions');
                    }
                    const key = `index-${(_a = suggestion.label) !== null && _a !== void 0 ? _a : suggestion[customSuggestionKey !== null && customSuggestionKey !== void 0 ? customSuggestionKey : '']}`;
                    if (suggestion.header && suggestion.label) {
                        return _jsx(DropdownHeader, { label: suggestion.label }, key);
                    }
                    const renderFunction = renderSuggestion ? renderSuggestion : renderDefaultSuggestion;
                    return (_jsx(MenuItem, { active: index === highlightedItemIndex, disabled: suggestion.disabled, value: suggestion.customSuggestion ? suggestion.customSuggestion : renderFunction(suggestion), onSelect: (_, event) => onSuggestionClicked(event, suggestion), index: index }, key));
                })] })));
    };
    const classes = classNames('AutoSuggest', 'dropdown', isOpen && 'open', dropDirection.dropup && 'dropup', className);
    return (_jsxs("div", Object.assign({}, remainingProps, { className: classes, onKeyDown: handleKeyDown, ref: componentRef }, { children: [renderInput(), renderDropdownMenu()] })));
};
export default AutoSuggest;
